import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// ===== Types =====

interface InviteLink {
  id: string;
  token: string;
  tenantId: string;
  targetUserType: string;
  status: string;
  personName?: string;
  description?: string;
  inviteUrl: string;
  createdAt: string;
  usedAt?: string;
  expiresAt?: string;
  createdBy: { name: string; email: string };
  usedBy?: { name: string; email: string };
}

interface InviteStats {
  total: number;
  active: number;
  used: number;
  expired: number;
  revoked: number;
  pendingKyc: number;
}

interface InviteValidation {
  id: string;
  tenantId: string;
  tenantName: string;
  targetUserType: string;
  personName?: string;
  description?: string;
  createdByName: string;
  valid: boolean;
}

interface DocumentType {
  code: string;
  name: string;
  description: string;
  isRequired: boolean;
  displayOrder: number;
  allowedMimeTypes: string[];
  maxFileSizeMb: number;
}

interface UserDocument {
  id: string;
  documentType: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  status: string;
  rejectionReason?: string;
  createdAt: string;
}

interface PendingKycUser {
  userId: string;
  name: string;
  email: string;
  cpfCnpj: string;
  phone?: string;
  type: string;
  createdAt: string;
  documents: UserDocument[];
}

interface UseInviteResult {
  userId: string;
  name: string;
  email: string;
  type: string;
  tenantId: string;
  tenantName: string;
  accessToken: string;
  refreshToken: string;
  message: string;
}

// ===== Admin Hooks =====

export function useInviteLinks(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['invite-links', page, limit],
    queryFn: () =>
      api.get(`/invite-links?page=${page}&limit=${limit}`).then((r) => r.data),
  });
}

export function usePublicLinks(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['public-links', page, limit],
    queryFn: () =>
      api.get(`/invite-links/public?page=${page}&limit=${limit}`).then((r) => r.data),
  });
}

export function useInviteStats() {
  return useQuery<InviteStats>({
    queryKey: ['invite-links', 'stats'],
    queryFn: () => api.get('/invite-links/stats').then((r) => r.data),
  });
}

export function usePublicLinkStats() {
  return useQuery<{ total: number; active: number; used: number; expired: number; revoked: number; totalRegistrations: number }>({
    queryKey: ['public-links', 'stats'],
    queryFn: () => api.get('/invite-links/public/stats').then((r) => r.data),
  });
}

export function useCreateInviteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      personName?: string;
      description?: string;
      targetUserType?: string;
      expiresAt?: string;
      isPublic?: boolean;
      maxUses?: number;
    }) => api.post('/invite-links', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links'] });
      qc.invalidateQueries({ queryKey: ['public-links'] });
    },
  });
}

export function useRevokeInviteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch(`/invite-links/${id}/revoke`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links'] });
    },
  });
}

// ===== Admin KYC Review =====

export function usePendingKyc(page = 1, limit = 20) {
  return useQuery<{ items: PendingKycUser[]; total: number; page: number; totalPages: number }>({
    queryKey: ['invite-links', 'pending-kyc', page, limit],
    queryFn: () =>
      api.get(`/invite-links/pending-kyc?page=${page}&limit=${limit}`).then((r) => r.data),
  });
}

export function useApproveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.patch(`/invite-links/approve-user/${userId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links'] });
    },
  });
}

export function useRejectUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      api.patch(`/invite-links/reject-user/${userId}`, { reason }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links'] });
    },
  });
}

export function useResetUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.patch(`/invite-links/reset-user/${userId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links'] });
    },
  });
}

// ===== Public Hooks =====

export function useValidateInvite(token: string) {
  return useQuery<InviteValidation>({
    queryKey: ['invite-validate', token],
    queryFn: () =>
      api.get(`/invite-links/validate/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });
}

export function useDocumentTypes(tenantId?: string, accountType?: string) {
  const at = accountType || 'PF';
  return useQuery<DocumentType[]>({
    queryKey: ['invite-links', 'document-types', tenantId || 'fallback', at],
    queryFn: () => {
      const base = tenantId ? `/invite-links/document-types/${tenantId}` : '/invite-links/document-types';
      return api.get(`${base}?accountType=${at}`).then((r) => r.data);
    },
  });
}

export function useUseInviteLink() {
  return useMutation<UseInviteResult, Error, { token: string; data: { name: string; email: string; password: string; cpfCnpj?: string; phone?: string } }>({
    mutationFn: ({ token, data }) =>
      api.post(`/invite-links/use/${token}`, data).then((r) => r.data),
  });
}

// ===== Authenticated User Hooks (for document upload) =====

export function useMyDocuments() {
  return useQuery<UserDocument[]>({
    queryKey: ['invite-links', 'my-documents'],
    queryFn: () =>
      api.get('/invite-links/my-documents').then((r) => r.data),
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      return api.post('/invite-links/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links', 'my-documents'] });
    },
  });
}

export function useSubmitDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post('/invite-links/submit-documents').then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invite-links', 'my-documents'] });
    },
  });
}

// ===== Admin: View Document File =====

export function useDocumentFileUrl(documentId: string) {
  // Returns the URL to fetch the document file (admin only)
  const baseURL = api.defaults.baseURL || '';
  return `${baseURL}/invite-links/document-file/${documentId}`;
}
